import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle2, XCircle, Lightbulb } from 'lucide-react';
import { getSessionById, getMessagesBySessionId, getSynthesis } from '../lib/supabaseService';
import { generateSynthesis } from '../lib/openaiService';
import type { Database } from '../lib/database.types';

type Session = Database['public']['Tables']['sessions']['Row'];
type Message = Database['public']['Tables']['messages']['Row'];

interface Synthesis {
  intro: string;
  agreements: string[];
  disagreements: string[];
  suggestions: string[];
}

const SynthesisPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [session, setSession] = useState<Session | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [synthesis, setSynthesis] = useState<Synthesis | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!sessionId) return;

      try {
        const [sessionData, messagesData] = await Promise.all([
          getSessionById(sessionId),
          getMessagesBySessionId(sessionId)
        ]);

        setSession(sessionData);
        setMessages(messagesData);

        // Ensure we have both messages
        if (messagesData.length < 2) {
          navigate(`/session/${sessionId}`);
          return;
        }

        // First try to get existing synthesis
        let synthesisText = await getSynthesis(sessionId);

        // If no synthesis exists, generate it
        if (!synthesisText) {
          const messageTexts = messagesData
            .map(msg => msg.text_content || '')
            .filter(Boolean);

          synthesisText = await generateSynthesis(
            messageTexts,
            sessionData.context,
            sessionData.topic,
            sessionId
          );
        }

        // Parse the synthesis text
        const [intro, ...sections] = synthesisText.split(/\d️⃣/).map(s => s.trim());
        
        setSynthesis({
          intro: intro.split('\n')[0], // Get first line as intro
          agreements: sections[0]?.split('•').filter(Boolean).map(s => s.trim()) || [],
          disagreements: sections[1]?.split('•').filter(Boolean).map(s => s.trim()) || [],
          suggestions: sections[2]?.split('•').filter(Boolean).map(s => s.trim()) || []
        });
      } catch (err) {
        setError('Impossible de générer la synthèse');
        console.error('Error generating synthesis:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [sessionId, navigate]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-pulse text-slate-600">
          Génération de la synthèse...
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="text-center text-error p-4">
        {error || 'Session non trouvée'}
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto animate-fade-in">
      <h1 className="text-3xl font-bold text-center mb-8">
        Synthèse de la médiation
      </h1>

      <div className="card mb-6">
        <div className="space-y-2 text-slate-600">
          <p><strong>Contexte :</strong> {session.context}</p>
          <p><strong>Sujet :</strong> {session.topic}</p>
        </div>
      </div>

      {synthesis && (
        <div className="space-y-8">
          {synthesis.intro && (
            <div className="text-center text-lg text-slate-700 mb-8">
              {synthesis.intro}
            </div>
          )}

          <section className="card max-w-md mx-auto px-4 py-4">
            <div className="flex items-center gap-3 text-green-600 mb-4">
              <CheckCircle2 size={24} />
              <h2 className="text-xl font-semibold">Points d'accord</h2>
            </div>
            <ul className="space-y-2">
              {synthesis.agreements.map((point, index) => (
                <li key={index} className="text-slate-600">• {point}</li>
              ))}
            </ul>
          </section>

          <section className="card max-w-md mx-auto px-4 py-4">
            <div className="flex items-center gap-3 text-orange-600 mb-4">
              <XCircle size={24} />
              <h2 className="text-xl font-semibold">Points de désaccord</h2>
            </div>
            <ul className="space-y-2">
              {synthesis.disagreements.map((point, index) => (
                <li key={index} className="text-slate-600">• {point}</li>
              ))}
            </ul>
          </section>

          <section className="card max-w-md mx-auto px-4 py-4">
            <div className="flex items-center gap-3 text-sky-600 mb-4">
              <Lightbulb size={24} />
              <h2 className="text-xl font-semibold">Suggestions</h2>
            </div>
            <ul className="space-y-2">
              {synthesis.suggestions.map((suggestion, index) => (
                <li key={index} className="text-slate-600">• {suggestion}</li>
              ))}
            </ul>
          </section>
        </div>
      )}

      <div className="mt-8 text-center">
        <button
          onClick={() => navigate('/final')}
          className="btn-primary"
        >
          Terminer la médiation
        </button>
      </div>
    </div>
  );
};

export default SynthesisPage;